import { getInstanceDefinition } from '../cards/cardLookup';
import { parseAlwaysActiveTraits, isHeroLike, getEquippedWeaponIds } from '../cards/effectHelpers';

export function isWeaponEquipped(player, weaponInstanceId) {
  return Object.values(player.equippedWeapons).flat().includes(weaponInstanceId);
}

// Which hero (if any) currently has this specific weapon instance equipped -
// a hero can have up to 2 (Disowned Bloodrager), but a given weapon instance
// is only ever equipped to exactly one of them at a time (enforced by
// dungeonActions.equipWeapon's "already equipped to another hero" check).
export function heroForEquippedWeapon(player, weaponInstanceId) {
  return Object.keys(player.equippedWeapons).find((h) => player.equippedWeapons[h].includes(weaponInstanceId));
}

// Rulebook: "To equip a weapon, a hero must have Strength equal to or
// greater than the weapon's Weight." Shared by dungeonActions.js's
// equipWeapon (the one-time gate at equip time) and contributesTraits below
// (a LIVE re-check during combat - Matt reported this directly: "the
// effect of the Guardsman made me lose strength but it didn't make my
// regular drop his spear," per Guardsman's "Each hero suffers Strength -6"
// - see resolveCombat.js's ROYAL_GUARD_STRENGTH_PENALTY). Rulebook: "If a
// weapon is destroyed, or is somehow unequipped while fighting a monster,
// you lose the benefit of its traits... but not the benefit of any of its
// abilities that you have already used" - exactly this scenario, so
// contributesTraits (traits only) re-checks this every time, while already-
// used Dungeon abilities (usedAbilityInstanceIds) are untouched.
export function heroMeetsWeaponWeight(heroDef, weaponDef) {
  // Dwarven Bear Hammer: "Dwarf heroes of any Strength may equip this
  // weapon." / Numenera, Dart Thrower: "Rangers of any Strength may equip
  // this weapon."
  const strengthBypassed = (weaponDef.id === 'village-dwarven-bear-hammer' && heroDef.traits.includes('Dwarf'))
    || (weaponDef.id === 'village-dart-thrower' && heroDef.traits.includes('Ranger'));
  if (strengthBypassed) return true;
  // Worlds Collide, Chalice Mace: "Clerics require 3 less Strength to
  // equip."
  const effectiveWeight = (weaponDef.id === 'village-chalice-mace' && heroDef.traits.includes('Cleric'))
    ? Math.max(0, (weaponDef.weight || 0) - 3)
    : (weaponDef.weight || 0);
  return (heroDef.strength || 0) >= effectiveWeight;
}

// The Djinni/Efreet group: each is immune to one hero class (that hero's own
// attack contribution is voided) and, for two of them, a matching weapon
// type (voided regardless of which hero it's equipped to). "Immune to
// clerics" (Iblis) has no weapon half - clerics aren't tied to a weapon type
// the way rangers/bows or fighters/edged weapons are.
// Into the Abyss, Bolt of Purity: "Magic Attack +1 against monsters that
// give diseases." "Gives diseases" isn't a printed trait/keyword anywhere
// in the card pool (unlike every other monster-conditional bonus, which
// keys off a monster's group/traits/id directly) - a derived property, so
// this is a hand-curated static lookup (same shape as MONSTER_IMMUNITIES/
// DEFEAT_CONDITIONS below), built by scanning every monster's own printed
// Battle/Aftermath text for "gain 1/2 disease(s)" (this engine's disease =
// curse convention - see effectHelpers.js's gainCurse). Cross-checked
// against cardDefinitions.generated.js directly (deduplicated - no risk of
// missing a physical copy's own separately-worded text).
const MONSTERS_THAT_GIVE_DISEASES = new Set([
  'monster-blood-barm', 'monster-bloodbound', 'monster-broken-hound', 'monster-death-polyps',
  'monster-doomspawn', 'monster-filth-queen', 'monster-ghost-toxin', 'monster-hungry-dead',
  'monster-ithsyn', 'monster-margr', 'monster-plague-zombie', 'monster-plaguebearer',
  'monster-poison-skeleton', 'monster-restless-corpse', 'monster-seskii', 'monster-snakewyrm',
  'monster-spider-bloat', 'monster-spike-lasher', 'monster-the-unchained', 'monster-thuman',
  'monster-tomb-haunt', 'monster-walking-scourge', 'monster-warrior-host', 'monster-zombie-mob',
]);

const MONSTER_IMMUNITIES = {
  'monster-ifrit': { heroClass: 'Ranger', weaponType: 'Bow' },
  'monster-marid': { heroClass: 'Thief' },
  'monster-majnun': { heroClass: 'Wizard', cardTrait: 'Spell' },
  'monster-ghul': { heroClass: 'Fighter', weaponType: 'Edged' },
  'monster-iblis': { heroClass: 'Cleric' },
  'monster-withered-dryad': { weaponType: 'Blunt' },
  // Caverns of Bane, Ooze/Cavern. weaponTypes (plural, array) is the
  // multi-type sibling of weaponType (singular) above - Green/Black Viscoid
  // are immune to two weapon types at once, which the original ToR-only
  // immunity table never needed to express. voidAttackType is a different
  // KIND of immunity, applied once to the whole subtotal in
  // computeBaseAttack below rather than per-card here - "Immune to Physical/
  // Magic Attack" zeroes that entire subtotal UNCONDITIONALLY, regardless of
  // which card(s) contributed it (including ability-granted bonuses, not
  // just printed trait lines) and regardless of the OTHER subtotal.
  // requiresAttackType (below) is the DIFFERENT, CONDITIONAL sibling for
  // "X Attack required" wording - Matt, correcting an earlier misreading
  // that had conflated the two: "required" means the OTHER subtotal only
  // counts if this one is present, not "always zero the other subtotal."
  'monster-blue-viscoid': { weaponTypes: ['Edged', 'Polearm'], voidAttackType: 'magic' },
  'monster-green-viscoid': { weaponTypes: ['Edged', 'Polearm'] },
  'monster-black-viscoid': { weaponTypes: ['Edged', 'Polearm'] },
  'monster-greater-viscoid': { voidAttackType: 'magic' },
  'monster-mucilaginous-mass': { voidAttackType: 'magic' },
  // "Magic Attack required." (not "Immune to Physical Attack") - Physical
  // still counts in full whenever Magic is also present; see
  // requiresAttackType's own comment above.
  'monster-legion-sorcerer': { requiresAttackType: 'magic' },
  'monster-red-viscoid': { requiresAttackType: 'magic' },
  'monster-stalking-stalactite': { requiresAttackType: 'magic' },
  'monster-mancatcher-worm': { requiresAttackType: 'magic' },
  'monster-cavernmaw': { voidAttackType: 'physical' }, // "Immune to Physical Attack" - genuinely unconditional, unlike the "required" group above
  'bearer-mowtil-djinni-lich': { requiresAttackType: 'magic' }, // "Magic Attack required"
  // Root of Corruption, Elemental Earth. maxHeroStrength (below) is a new
  // KIND of immunity - unlike heroClass/weaponType (which key off a printed
  // trait), Tremorfiend voids a hero's contribution based on its numeric
  // Strength stat.
  'monster-rockslide': { voidAttackType: 'magic' }, // "Immune to Magic Attack"
  'monster-aftershock': { requiresAttackType: 'magic' }, // "Magic Attack required"
  'monster-eruptor': { requiresAttackType: 'magic' }, // "Magic Attack required"
  'monster-scree': { weaponType: 'Edged' }, // "Immune to edged weapons"
  'monster-tremorfiend': { maxHeroStrength: 4 }, // "Immune to heroes with 4 Strength or less"
  'monster-guard-captain': { voidAttackType: 'magic' }, // "Immune to Magic Attack"
  // Root of Corruption, War Incarnate: printed text has TWO specialEffects
  // lines - "You can only select this monster to fight if you have an
  // equipped hero." (dungeon/fightEligibility.js's FIGHT_ELIGIBILITY) AND
  // "Immune to Magic Attack." (this entry) - both independently real, not
  // an either/or. Missing from source data until an audit pass caught it
  // via a direct card-scan read (docs/expansion-audit.md).
  'monster-war-incarnate': { voidAttackType: 'magic' }, // "Immune to Magic Attack"
  'monster-chirog': { voidAttackType: 'magic' }, // "Immune to Magic Attack"
  'monster-erynth-grask': { requiresAttackType: 'magic' }, // "Magic Attack Required"
  'monster-tetrahydra': { requiresAttackType: 'magic' }, // "Magic Attack Required"
  // Numenera, Ithsyn: "Immune to weapons other than bows" - the inverse of
  // every other weaponType immunity (those void ONE type; this voids every
  // type EXCEPT one). A distinct field so isImmuneToCard doesn't need to
  // enumerate every other weapon type to express "not this one."
  'monster-ithsyn': { weaponTypeExcept: 'Bow' },
  // Numenera, Night Howler/Edacious Destroyer: "Immune to Magic Attack or
  // Physical Attack, whichever is higher/lower." Unlike every other
  // voidAttackType entry (which unconditionally zeroes ONE fixed subtotal),
  // this is decided dynamically per-fight by comparing the two computed
  // subtotals - see computeBaseAttack's own handling of 'higherOf'/'lowerOf'
  // below (isImmuneToCard/contributesTraits never see these values, since
  // they can't be resolved per-card).
  'monster-night-howler': { voidAttackType: 'higherOf' },
  'monster-edacious-destroyer': { voidAttackType: 'lowerOf' },
  // Numenera, Sarrak (Thunderstone Bearer): "Ignore heroes of level 2 and
  // lower." Same shape as Tremorfiend's maxHeroStrength above, keyed off
  // level instead of Strength - "ignore" here just means "void this hero's
  // own attack contribution," the same practical effect as any other
  // MONSTER_IMMUNITIES entry.
  'bearer-sarrak': { maxHeroLevel: 2 },
  // Starter set, Vampiric Minion: "Immune to Regulars." Keyed off a
  // specific card id rather than a trait/class/stat - the first immunity
  // needing that shape.
  'monster-vampiric-minion': { cardId: 'basic-regular' },
  // Caverns of Bane, Legion Warrior: "Immune to unequipped heroes." Unlike
  // every other entry above (which void a MATCHING card's contribution
  // based on a fixed class/trait/stat), this reverses the normal rule that
  // a hero's own contribution applies whenever simply present, no equip
  // step required - checked separately below (isImmuneToCard has no access
  // to `player`/equip state, only the card's own def).
  'monster-legion-warrior': { requiresEquippedHero: true },
  'monster-hulking-yeti': { requiresAttackType: 'magic' }, // "Magic Attack Required"
  // Into the Abyss, Abyssal Elite/Undead Lich/Elemental Pain attack-type
  // immunities/requirements - same voidAttackType/requiresAttackType
  // mechanism as every entry above.
  'monster-archduke-of-pain': { requiresAttackType: 'magic' }, // "Magic Attack required"
  'monster-lich-lord': { voidAttackType: 'magic' }, // "Immune to Magic Attack"
  'monster-grave-knight': { voidAttackType: 'magic' },
  'monster-tomb-haunt': { voidAttackType: 'magic' },
  'monster-deathbringer': { voidAttackType: 'magic' },
  'monster-destiny': { voidAttackType: 'magic' },
  'monster-smoke-torment': { voidAttackType: 'physical' }, // "Immune to Physical Attack"
  'monster-shadow-torment': { voidAttackType: 'physical' },
  // Root of Corruption, Decay Incarnate: "Physical Attack required. Magic
  // Attack required." (2 separate printed lines, both conditions applying
  // to each other) - Matt: "you have to have both of them in your attack."
  // requiresAttackType: 'both' voids EITHER subtotal if the OTHER is 0,
  // netting out to "both subtotals count in full, or neither does" - see
  // computeBaseAttack's own handling.
  'monster-decay-incarnate': { requiresAttackType: 'both' },
  // Worlds Collide: same voidAttackType/requiresAttackType/weaponType/
  // requiresEquippedHero mechanisms as every entry above.
  'monster-regicide': { voidAttackType: 'magic' }, // "Immune to Magic Attack"
  'monster-usurper': { voidAttackType: 'magic' }, // "Immune to Magic Attack" (untranscribed until the 2026-08-18 scan re-check - see source-data/monsters.js)
  'monster-ebon-fume': { voidAttackType: 'magic' }, // "Immune to Magic Attack"
  'monster-revenant': { requiresAttackType: 'magic' }, // "Magic Attack required"
  'monster-noxious-slag': { weaponType: 'Edged' }, // "Immune to edged weapons"
  'monster-darkness': { requiresEquippedHero: true }, // "Ignore unequipped heroes"
  'monster-skywyrm-scavenger': { heroClasses: ['Thief', 'Ranger'] }, // "Ignore thieves and rangers" (title typo "Scavanger" fixed at the source, WC audit 2026-07-22 - see docs/expansion-audit.md)
  'monster-skywyrm-warrior': { heroClass: 'Fighter' }, // "Ignore fighters"
  'monster-skywyrm-priest': { heroClass: 'Cleric' }, // "Ignore clerics"
};

// Numenera, Ultraterrestrial group: "You cannot defeat this card with N or
// higher Total Attack Value" / "...unless you have exactly N Total Attack
// Value." A defeat GATE independent of the normal Health comparison -
// checked in combat/resolveCombat.js alongside `won`, not here (this table
// lives here only because it's conceptually paired with the immunity table
// above - both are "monster resists being beaten in an unusual way").
export const DEFEAT_CONDITIONS = {
  'monster-ellnoica': (totalAttack) => totalAttack < 10,
  'monster-nibovian-wife': (totalAttack) => totalAttack < 10,
  'monster-pallone': (totalAttack) => totalAttack < 9,
  'monster-abykos': (totalAttack) => totalAttack < 10,
  'monster-jurulisk': (totalAttack) => totalAttack === 8,
};

// Into the Abyss, Veris: "Add [Physical Attack +N and] Light +N to each
// equipped weapon" (L1: Light only; L2-L4: both, N scaling with level). An
// aura granted by every Veris copy PRESENT IN HAND onto every OTHER
// equipped weapon present, not a bonus to Veris's own card - the mirror
// image of Snakehead Flail/Liveoak Staff's "look up my own equipped hero"
// pattern in contributesTraits below (this looks up ANY Veris present
// instead). The physicalAttack half is applied per equipped weapon in
// contributesTraits; the light half is applied per equipped weapon in
// lightDarkness.js's computeTotalLight (light contributions are read
// there, not from this module's traits.light, which nothing downstream in
// computeBaseAttack ever consumes).
// See the "Undead Spectral group" comment inside computeBaseAttack below.
// Exported (not just module-local) so DungeonHall.vue can prompt "pay the
// cost or stay immune?" before dispatching CHOOSE_MONSTER - see that
// component's own comment. Brian reported this directly: "The Wight says
// it is immune to attack unless you discard 2 xp, but when I click it to
// fight I don't get the option to discard those xp." The engine-side half
// (resolveCombat.js's UNDEAD_SPECTRAL_COSTS, choices.payUndeadSpectralCost)
// was already complete - only the client-side prompt to actually opt in
// was missing, so these 5 monsters were unconditionally unfightable.
export const UNDEAD_SPECTRAL_IMMUNITY = new Set([
  'monster-souleater', 'monster-cruel-apparition', 'monster-will-o-the-wisp', 'monster-wight', 'monster-devourer',
]);

export const VERIS_WEAPON_BONUS = {
  'hero-veris-l1': { physicalAttack: 0, light: 1 },
  'hero-veris-l2': { physicalAttack: 1, light: 1 },
  'hero-veris-l3': { physicalAttack: 2, light: 2 },
  'hero-veris-l4': { physicalAttack: 3, light: 3 },
};

function isImmuneToCard(def, immunity) {
  if (!immunity) return false;
  if (def.type === 'hero' && immunity.heroClass && def.traits.includes(immunity.heroClass)) return true;
  // Worlds Collide, Skywyrm Scavenger/Warrior/Priest: "Ignore thieves and
  // rangers." / "Ignore fighters." / "Ignore clerics." - heroClasses
  // (plural) is the "more than one class at once" sibling of heroClass
  // above, same relationship as weaponTypes/weaponType below.
  if (def.type === 'hero' && immunity.heroClasses && immunity.heroClasses.some((c) => def.traits.includes(c))) return true;
  if (def.type === 'hero' && immunity.maxHeroStrength !== undefined && (def.strength || 0) <= immunity.maxHeroStrength) return true;
  if (def.type === 'hero' && immunity.maxHeroLevel !== undefined && (def.level ?? 1) <= immunity.maxHeroLevel) return true;
  if (def.traits.includes('Weapon')) {
    if (immunity.weaponType && def.traits.includes(immunity.weaponType)) return true;
    if (immunity.weaponTypes && immunity.weaponTypes.some((t) => def.traits.includes(t))) return true;
    if (immunity.weaponTypeExcept && !def.traits.includes(immunity.weaponTypeExcept)) return true;
  }
  if (immunity.cardTrait && def.traits.includes(immunity.cardTrait)) return true;
  if (immunity.cardId && def.id === immunity.cardId) return true;
  return false;
}

function contributesTraits(state, player, instanceId, {
  hasDarkness, monsterVP, monsterXP, monsterId, hallRankIndex, monsterHasBattle, monsterHasAftermath,
} = {}) {
  const def = getInstanceDefinition(state, instanceId);
  const isWeapon = def.traits.includes('Weapon');
  // Matt reported this directly: "the effect of the Guardsman made me lose
  // strength but it didn't make my regular drop his spear (which it should
  // have done)." Rulebook: "If a weapon is destroyed, or is somehow
  // unequipped while fighting a monster, you lose the benefit of its
  // traits... but not the benefit of any of its abilities that you have
  // already used." A Strength-reducing Battle effect (Guardsman's own
  // "-6," the wider Royal Guard family - resolveCombat.js's
  // ROYAL_GUARD_STRENGTH_PENALTY) is exactly this "somehow unequipped"
  // case - equippedWeapons itself is untouched (no state mutation, no
  // effect on abilities already used, matching the rulebook's own carve-
  // out), but a weapon whose hero no longer meets its Weight stops
  // contributing its traits for the rest of THIS combat, live, via
  // heroMeetsWeaponWeight re-checking current (possibly just-reduced)
  // Strength rather than trusting the one-time gate equipWeapon already
  // enforced back when it was first equipped.
  let equipped = isWeaponEquipped(player, instanceId);
  if (equipped && isWeapon) {
    const heroInstanceId = heroForEquippedWeapon(player, instanceId);
    const heroDef = heroInstanceId ? getInstanceDefinition(state, heroInstanceId) : null;
    if (heroDef && !heroMeetsWeaponWeight(heroDef, def)) equipped = false;
  }
  if (isImmuneToCard(def, MONSTER_IMMUNITIES[monsterId])) return null;
  // Legion Warrior's requiresEquippedHero (see MONSTER_IMMUNITIES above):
  // isHeroLike (not def.type === 'hero') so the Regular basic card is
  // covered too, same convention as every other "is this a hero" check.
  if (MONSTER_IMMUNITIES[monsterId]?.requiresEquippedHero && isHeroLike(def) && !getEquippedWeaponIds(player, instanceId).length) {
    return null;
  }
  // Root of Corruption, War Incarnate's Trophy ("Ignore 1 hero with the
  // highest Strength" - see effectHelpers.js's markInstanceIgnored):
  // "cannot contribute to the combat in any way, providing no Attack,
  // Light... or any other benefit," for the rest of the turn.
  if (player.turnFlags.ignoredInstanceIds?.includes(instanceId)) return null;
  // Worlds Collide, Void Apocalypse: "Choose 1 copy of each hero present (by
  // card title). Ignore all other heroes." Every copy of the same hero
  // title has identical printed stats, so which specific copy is "chosen"
  // never changes the outcome - deterministically keeps the FIRST copy (by
  // hand order) active and voids the rest, rather than adding a real
  // player-choice UI for a choice with no actual strategic difference.
  if (monsterId === 'monster-void-apocalypse' && isHeroLike(def)) {
    const firstOfTitle = player.hand.find((id) => getInstanceDefinition(state, id).id === def.id);
    if (firstOfTitle !== instanceId) return null;
  }
  // Dancing Sword: "Physical Attack +2" (normal, equip-gated) / "Even if not
  // equipped, Magic Attack +2 (and still provides light)" (explicitly exempt
  // from the equip gate below) - the only ToR weapon with a per-line
  // exception, so it's handled entirely here rather than through
  // parseAlwaysActiveTraits (whose regexes require the whole line to match,
  // and won't match this one anyway).
  if (def.id === 'village-dancing-sword') {
    return {
      physicalAttack: equipped ? 2 : 0, magicAttack: 2, attackReduction: 0, light: 0,
    };
  }
  // Rulebook: a Weapon's trait bonuses only apply while equipped (to any one
  // hero present - a player can equip multiple heroes with different weapons in
  // the same hand); every other card type (Hero, Item, Spell, Villager, Special)
  // applies automatically whenever "present" (i.e. in hand) - no equip step
  // required.
  if (isWeapon && !equipped) return null;
  const traits = parseAlwaysActiveTraits(def.abilityText.alwaysActive);
  // Veris: "Add Physical Attack +N to each equipped weapon" (see
  // VERIS_WEAPON_BONUS above) - summed across every Veris copy present,
  // applied to every equipped weapon (including Veris's own, if any).
  if (isWeapon) {
    for (const id of player.hand) {
      traits.physicalAttack += VERIS_WEAPON_BONUS[getInstanceDefinition(state, id).id]?.physicalAttack || 0;
    }
  }
  // Snakehead Flail: "Additional Magic Attack equal to the level of the
  // equipped hero."
  if (def.id === 'village-snakehead-flail') {
    const heroInstanceId = heroForEquippedWeapon(player, instanceId);
    const heroDef = heroInstanceId ? getInstanceDefinition(state, heroInstanceId) : null;
    traits.magicAttack += heroDef?.level || 0;
  }
  // Caverns of Bane, Liveoak Staff: "Subtract the Strength of the equipped
  // hero from this card's Physical Attack." Mirrors Snakehead Flail's own
  // equipped-hero-lookup above; allowed to go negative here (the subtotal
  // floors at 0 later, in computeBaseAttack).
  if (def.id === 'village-liveoak-staff') {
    const heroInstanceId = heroForEquippedWeapon(player, instanceId);
    const heroDef = heroInstanceId ? getInstanceDefinition(state, heroInstanceId) : null;
    traits.physicalAttack -= heroDef?.strength || 0;
  }
  // Root of Corruption, Liveoak Bow: "Subtract the selected monster's rank
  // from this card's Attack." Only knowable once a monster is chosen, same
  // timing constraint as Caliginite's Darkness bonus above - hallRankIndex
  // is 0-based, rank is 1-based.
  if (def.id === 'village-liveoak-bow' && hallRankIndex !== undefined) {
    traits.physicalAttack -= hallRankIndex + 1;
  }
  // Caliginite Lurker/Prowler: "Add Physical Attack +2/+3 if there is
  // Darkness." Darkness depends on the monster's actual rank, which is only
  // known once combat is being resolved (see the hasDarkness param) - unlike
  // Caliginite Silencer's own Dungeon-timing ability (hero-caliginite-l3),
  // which resolves before a monster is chosen and so can't use this shortcut.
  if (hasDarkness && def.id === 'hero-caliginite-l1') traits.physicalAttack += 2;
  if (hasDarkness && def.id === 'hero-caliginite-l2') traits.physicalAttack += 3;
  // Numenera, Murden: "Trophy: If there is Darkness in this rank, add
  // Physical Attack +2 to each thief." See turnFlags.js's own comment on
  // murdenTrophyActive - the Trophy's mandatory trigger just sets a flag;
  // the actual Darkness condition (this fight's real hasDarkness) and the
  // per-Thief scaling (this card's own traits, once per Thief card present)
  // are both resolved here, same shape as Caliginite's own Darkness bonus
  // just above.
  if (hasDarkness && player.turnFlags.murdenTrophyActive && def.traits.includes('Thief')) {
    traits.physicalAttack += 2;
  }
  // Sternnkin Ettinbane/Dragonbane: "Add Physical Attack +2/+3 when fighting
  // monsters with VP greater than this card's Strength." Only knowable once
  // a monster is chosen (see the monsterVP param), same timing constraint as
  // the Caliginite Darkness bonus above.
  if (monsterVP !== undefined && monsterVP > (def.strength || 0)) {
    if (def.id === 'hero-sternnkin-l2') traits.physicalAttack += 2;
    if (def.id === 'hero-sternnkin-l3') traits.physicalAttack += 3;
  }
  // Numenera, Graceful/Graceful Glaive/Enhanced Graceful Glaive: "Add
  // Physical Attack +[2/4/5] while fighting a monster with a Battle
  // [/Aftermath] effect." Only knowable once a monster is chosen, same
  // timing constraint as Caliginite/Sternnkin above.
  if (monsterHasBattle && def.id === 'hero-graceful-l1') traits.physicalAttack += 2;
  if (monsterHasBattle && def.id === 'hero-graceful-l2') traits.physicalAttack += 4;
  if ((monsterHasBattle || monsterHasAftermath) && def.id === 'hero-graceful-l3') traits.physicalAttack += 5;
  // Numenera, Mystic Glaive/Enhanced Mystic Glaive: "Convert this hero's
  // Physical Attack to Magic Attack." A per-instance opt-in flag set by the
  // card's own Dungeon ability (see effectRegistry/heroes.js) - swaps this
  // specific card's own contribution rather than the whole player's total
  // (contrast Tincture of Victims' "convert ALL," flagged as a gap since
  // Dungeon abilities fire before any total is computed at all).
  if (player.turnFlags.convertedToMagicInstanceIds?.includes(instanceId)) {
    traits.magicAttack += traits.physicalAttack;
    traits.physicalAttack = 0;
  }
  // Glamercast Troubadour/Bard/Maestro: "Give each other hero Magic Attack
  // +[1/1/2]." Modeled as a flat addition to the shared Magic Attack pool
  // (this engine tracks combined totals, not per-hero attack) sized to the
  // number of *other* hero cards present - equivalent in total to buffing
  // each of them individually.
  if (def.id === 'hero-glamercast-l1' || def.id === 'hero-glamercast-l2') {
    const otherHeroCount = player.hand.filter((id) => id !== instanceId && isHeroLike(getInstanceDefinition(state, id))).length;
    traits.magicAttack += otherHeroCount * 1;
  }
  if (def.id === 'hero-glamercast-l3') {
    const otherHeroCount = player.hand.filter((id) => id !== instanceId && isHeroLike(getInstanceDefinition(state, id))).length;
    traits.magicAttack += otherHeroCount * 2;
  }
  // Forcemage class: "Add Magic Attack +1 for each spell present."
  if (def.id === 'hero-forcemage-l1') {
    const spellCount = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Spell')).length;
    traits.magicAttack += spellCount;
  }
  // Spellsword Venturer/Challenger/Vanquisher: "Add Magic Attack +1 if at
  // least 1 magic item, magic weapon, or spell is present" (L1, a binary
  // bonus, not per-count) / "...+1/+2 for each magic item, magic weapon, and
  // spell present" (L2/L3, per-count).
  if (def.id === 'hero-spellsword-l1' || def.id === 'hero-spellsword-l2' || def.id === 'hero-spellsword-l3') {
    const magicItemWeaponSpellCount = player.hand.filter((id) => {
      const t = getInstanceDefinition(state, id).traits || [];
      return t.includes('Spell') || (t.includes('Magic') && (t.includes('Item') || t.includes('Weapon')));
    }).length;
    if (def.id === 'hero-spellsword-l1') traits.magicAttack += magicItemWeaponSpellCount > 0 ? 1 : 0;
    if (def.id === 'hero-spellsword-l2') traits.magicAttack += magicItemWeaponSpellCount * 1;
    if (def.id === 'hero-spellsword-l3') traits.magicAttack += magicItemWeaponSpellCount * 2;
  }
  // Numenera, Bio-energy Cutter: "Magic Attack equal to the XP of the
  // monster selected to fight." Only knowable once a monster is chosen,
  // same timing constraint as every other monster*-conditional entry above.
  if (def.id === 'village-bio-energy-cutter' && monsterXP !== undefined) {
    traits.magicAttack += monsterXP;
  }
  // Numenera, Dart Thrower: "Physical Attack +4 against rank 2 or higher." /
  // "Rangers of any Strength may equip this weapon. While equipped to a
  // ranger, this weapon's Attack can apply to rank 1" - i.e. the rank
  // restriction above is waived specifically when equipped to a Ranger
  // (the Strength bypass itself is handled in actions/dungeonActions.js's
  // equipWeapon, alongside Dwarven Bear Hammer's own bypass).
  if (def.id === 'village-dart-thrower' && hallRankIndex !== undefined) {
    const heroInstanceId = heroForEquippedWeapon(player, instanceId);
    const heroDef = heroInstanceId ? getInstanceDefinition(state, heroInstanceId) : null;
    const isRanger = heroDef?.traits.includes('Ranger');
    if (hallRankIndex >= 1 || isRanger) traits.physicalAttack += 4;
  }
  // Starter set, Bardiche: "Add Physical Attack +1 while attacking a
  // monster in Rank 1." hallRankIndex is 0-based; rank 1 = index 0.
  if (def.id === 'village-bardiche' && hallRankIndex === 0) {
    traits.physicalAttack += 1;
  }
  // Worlds Collide, Jondul Bow: "Physical Attack +4 against rank 2 or
  // higher." Same shape as Dart Thrower above, minus its Ranger-equipped
  // rank-1 waiver (Jondul Bow's text has no such exception).
  if (def.id === 'village-jondul-bow' && hallRankIndex !== undefined && hallRankIndex >= 1) {
    traits.physicalAttack += 4;
  }
  // Into the Abyss, Blind Grandmaster/Shihan: "Add Magic Attack +3/+4 if no
  // Light items are present." A hand-wide condition (not per-card), so
  // checked directly against `player.hand` here rather than the card's own
  // instance - matches the "some/every card present" checks already done
  // inline for Glamercast/Forcemage/Spellsword above.
  if ((def.id === 'hero-blind-l3' || def.id === 'hero-blind-l4')) {
    const hasLightItem = player.hand.some((id) => {
      const t = getInstanceDefinition(state, id).traits || [];
      return t.includes('Item') && t.includes('Light');
    });
    if (!hasLightItem) traits.magicAttack += def.id === 'hero-blind-l3' ? 3 : 4;
  }
  // Worlds Collide, Woodfolk Herbalist/Shaman: "specialEffects: Add Magic
  // Attack +[3/4] against monsters that give diseases." A per-card
  // alwaysActive check (unlike Bolt of Purity's own disease-conditional
  // bonus, which is a Dungeon-ability GRANT requiring activation - this
  // applies automatically just by being present) - reuses this module's own
  // MONSTERS_THAT_GIVE_DISEASES lookup.
  const WOODFOLK_DISEASE_MAGIC_BONUS = { 'hero-woodfolk-l1': 3, 'hero-woodfolk-l2': 4 };
  if (WOODFOLK_DISEASE_MAGIC_BONUS[def.id] && monsterId && MONSTERS_THAT_GIVE_DISEASES.has(monsterId)) {
    traits.magicAttack += WOODFOLK_DISEASE_MAGIC_BONUS[def.id];
  }
  // Worlds Collide, Stoneguard Brute/Bruiser/Tanker/Juggernaut: "specialEffects:
  // Add Physical Attack +N while equipped[. Add Physical Attack +M for each
  // mercenary present.]" "While equipped" on a HERO card (not a weapon) most
  // plausibly means "while THIS hero itself has a weapon equipped" - a
  // documented reading, not confirmed against a rulebook (see
  // docs/expansion-audit.md). "Mercenary" is a real printed trait on
  // several Villager-type village cards, not a special equip slot -
  // "present" means in hand, this engine's standing convention. Brute (L1)'s
  // own "+1 while equipped" line was missing entirely from source data until
  // the WC numeric-stat audit (2026-07-22) found it on the card scan - it has
  // no mercenary-bonus line (that starts at L2), so it's absent from
  // STONEGUARD_MERC_BONUS below.
  const STONEGUARD_EQUIPPED_BONUS = {
    'hero-stoneguard-l1': 1, 'hero-stoneguard-l2': 2, 'hero-stoneguard-l3': 3, 'hero-stoneguard-l4': 5,
  };
  const STONEGUARD_MERC_BONUS = { 'hero-stoneguard-l2': 1, 'hero-stoneguard-l3': 1, 'hero-stoneguard-l4': 2 };
  if (STONEGUARD_EQUIPPED_BONUS[def.id] && getEquippedWeaponIds(player, instanceId).length > 0) {
    traits.physicalAttack += STONEGUARD_EQUIPPED_BONUS[def.id];
  }
  if (STONEGUARD_MERC_BONUS[def.id]) {
    const mercCount = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Mercenary')).length;
    if (mercCount) traits.physicalAttack += STONEGUARD_MERC_BONUS[def.id] * mercCount;
  }
  // Into the Abyss, Thornwood group: "Add Physical Attack +[1/1/2/3] for each
  // rank of the monster you are fighting." Only knowable once a monster is
  // chosen, same timing constraint as Caliginite/Sternnkin/Dart Thrower
  // above. hallRankIndex is 0-based; "rank" in the printed text is 1-based
  // (rank 1 = index 0), so the bonus uses hallRankIndex + 1.
  const THORNWOOD_PER_RANK_BONUS = {
    'hero-thornwood-l1': 1, 'hero-thornwood-l2': 1, 'hero-thornwood-l3': 2, 'hero-thornwood-l4': 3,
  };
  const thornwoodBonus = THORNWOOD_PER_RANK_BONUS[def.id];
  if (thornwoodBonus && hallRankIndex !== undefined) {
    traits.physicalAttack += thornwoodBonus * (hallRankIndex + 1);
  }
  // Into the Abyss, Thornwood Hunter (L2): "Add Light +1 against the highest
  // rank." NOT handled here - this function's own `traits.light` field is
  // never consumed by computeBaseAttack below (only physicalAttack/
  // magicAttack/attackReduction are summed), so a card's Light contribution
  // has to be applied where Light is ACTUALLY read from instead - see
  // lightDarkness.js's computeTotalLight (hallRankIndex-aware, same
  // highest-rank check).
  // Into the Abyss, Polearm: "Add Physical Attack +4 while equipped to a
  // hero with Strength 8 or higher." Mirrors Snakehead Flail/Liveoak
  // Staff's own equipped-hero-lookup pattern above.
  if (def.id === 'village-polearm') {
    const heroInstanceId = heroForEquippedWeapon(player, instanceId);
    const heroDef = heroInstanceId ? getInstanceDefinition(state, heroInstanceId) : null;
    if ((heroDef?.strength || 0) >= 8) traits.physicalAttack += 4;
  }
  // Into the Abyss, Short Bow: "Physical Attack +3 against rank 2 or
  // higher." / "Add Physical Attack +2 while equipped to a ranger." Same
  // shape as Dart Thrower's own rank-conditional + Ranger-equipped bonuses.
  if (def.id === 'village-short-bow') {
    if (hallRankIndex !== undefined && hallRankIndex >= 1) traits.physicalAttack += 3;
    const heroInstanceId = heroForEquippedWeapon(player, instanceId);
    const heroDef = heroInstanceId ? getInstanceDefinition(state, heroInstanceId) : null;
    if (heroDef?.traits.includes('Ranger')) traits.physicalAttack += 2;
  }
  return traits;
}

// Physical/Magic Attack subtotals from cards present in hand, before Darkness is
// applied (rulebook: each subtotal individually floors at 0; Darkness is a single
// flat reduction on the combined total, applied separately in lightDarkness.js).
// Ambiguous "Attack -N" (not specified Physical or Magic) reduces whichever
// subtotal is currently higher - a documented default standing in for the "the
// affected player chooses" rule until the UI can prompt for it (see plan, AI
// generic-fallback rationale for the same open design point).
// `totalLight`/`hallRankIndex` are optional - only resolveCombat.js (which
// already knows both once a monster is chosen) can supply them, enabling
// Darkness-conditional bonuses like Caliginite's. Callers that only need a
// hand's flat attack total (e.g. tests, or UI previews before a monster is
// picked) simply omit them and those bonuses don't apply, same as printed
// rules text: no Darkness is knowable before a monster is chosen.
export function computeBaseAttack(state, playerId, {
  targetMonsterGroup, monsterTraits, hallRankIndex, totalLight, monsterVP, monsterId, monsterHasBattle, monsterHasAftermath,
} = {}) {
  const player = state.players.find((p) => p.id === playerId);
  const hasDarkness = hallRankIndex !== undefined && totalLight !== undefined
    && (hallRankIndex + 1) - totalLight > 0;
  // bonusPhysicalAttack/bonusMagicAttack: written by Milestone 3 ability
  // handlers (e.g. "Add Physical Attack +2 for each hero present") that grant
  // attack dynamically rather than via a card's static printed trait line -
  // see engine/state/turnFlags.js.
  let physical = player.turnFlags.bonusPhysicalAttack || 0;
  let magic = player.turnFlags.bonusMagicAttack || 0;
  let attackReduction = 0;
  if (targetMonsterGroup) {
    for (const bonus of player.turnFlags.groupConditionalAttackBonuses) {
      if (bonus.group !== targetMonsterGroup) continue;
      if (bonus.type === 'magic') magic += bonus.amount;
      else physical += bonus.amount;
    }
  }
  // Numenera, Chemical Injector: "Add Magic Attack +2 to one hero against
  // Doomladen and Doomknights." Trait-conditional sibling of
  // groupConditionalAttackBonuses above (that one keys off the monster's
  // GROUP; this needs the monster's TRAITS instead) - granted at Dungeon-
  // ability-use time (before a monster is chosen), applied here once the
  // fought monster's traits are actually known.
  if (monsterTraits) {
    for (const bonus of player.turnFlags.traitConditionalAttackBonuses) {
      if (!monsterTraits.includes(bonus.trait)) continue;
      if (bonus.type === 'magic') magic += bonus.amount;
      else physical += bonus.amount;
    }
  }
  // Into the Abyss, Bolt of Purity: "Magic Attack +1 against monsters that
  // give diseases." Same "granted at Dungeon-ability-use time, applied
  // once the fought monster is actually known" shape as the two bonuses
  // above, but keyed off MONSTERS_THAT_GIVE_DISEASES (a derived property)
  // instead of group/trait, so it gets its own narrow turnFlag rather than
  // reusing groupConditionalAttackBonuses/traitConditionalAttackBonuses'
  // own shape for a condition neither can express.
  if (monsterId && MONSTERS_THAT_GIVE_DISEASES.has(monsterId)) {
    magic += player.turnFlags.bonusMagicAttackAgainstDiseaseGivers || 0;
  }
  for (const instanceId of player.hand) {
    const traits = contributesTraits(state, player, instanceId, {
      hasDarkness, monsterVP, monsterId, hallRankIndex, monsterHasBattle, monsterHasAftermath,
    });
    if (!traits) continue;
    physical += traits.physicalAttack;
    magic += traits.magicAttack;
    attackReduction += traits.attackReduction;
  }
  while (attackReduction > 0 && (physical > 0 || magic > 0)) {
    if (physical >= magic) physical -= 1;
    else magic -= 1;
    attackReduction -= 1;
  }
  // Root of Corruption, Tincture of Victims: "Convert all Magic Attack to
  // Physical Attack, or vice versa." (turnFlags.attackConversionDirection,
  // set by its Dungeon ability, fired before a monster is even chosen - see
  // effectRegistry/villages.js). Resolved here instead, once the party's own
  // Physical/Magic subtotals are fully assembled from every hand card plus
  // every other bonus above - the "no already-computed split exists yet at
  // Dungeon-ability time" sequencing problem this was flagged for turns out
  // not to matter: like every other turn-scoped ability grant (bonusPhysical
  // Attack, ignoreDarknessPenalty, etc.), the ability only needs to leave a
  // flag behind for computeBaseAttack to read once the total actually
  // exists. Applied BEFORE the monster's own immunities just below, so
  // those still see (and can still void) whatever the converted total
  // becomes - same as any other card's own "Attack becomes Magic Attack"
  // line (e.g. Goodberries).
  if (player.turnFlags.attackConversionDirection === 'toPhysical') { physical += magic; magic = 0; }
  if (player.turnFlags.attackConversionDirection === 'toMagic') { magic += physical; physical = 0; }
  const voidAttackType = MONSTER_IMMUNITIES[monsterId]?.voidAttackType;
  if (voidAttackType === 'physical') physical = 0;
  if (voidAttackType === 'magic') magic = 0;
  // Worlds Collide, Undead Spectral group (Souleater/Cruel Apparition/
  // Will-o'-the-Wisp/Wight/Devourer): "Immune to Physical Attack and Magic
  // Attack unless you [pay a cost] when you select this monster to fight."
  // A conditional WHOLE-FIGHT immunity (not a per-card contribution void
  // the way every other MONSTER_IMMUNITIES entry is), so it can't live in
  // that static table - depends on turnFlags.paidUndeadSpectralImmunity,
  // set in combat/resolveCombat.js right before this function is called,
  // per the choices.payUndeadSpectralCost flag on CHOOSE_MONSTER.
  if (UNDEAD_SPECTRAL_IMMUNITY.has(monsterId) && player.turnFlags.paidUndeadSpectralImmunity !== monsterId) {
    physical = 0;
    magic = 0;
  }
  // Numenera, Night Howler/Edacious Destroyer: decided dynamically per-fight
  // (see MONSTER_IMMUNITIES' own comment above) - ties go to physical
  // (arbitrary but harmless: zeroing either of two equal subtotals has the
  // same net effect).
  if (voidAttackType === 'higherOf') { if (magic > physical) magic = 0; else physical = 0; }
  if (voidAttackType === 'lowerOf') { if (magic < physical) magic = 0; else physical = 0; }
  // "Magic Attack required" / "Physical Attack required" - Matt, correcting
  // an earlier misreading: this is NOT the same as "Immune to X Attack"
  // (voidAttackType above, which unconditionally zeroes one subtotal
  // regardless of the other). "required" is CONDITIONAL: "Magic Attack
  // required" means Physical only counts if Magic is ALSO present (Magic
  // itself is unconditional); "the inverse is also true" for "Physical
  // Attack required." Root of Corruption's Decay Incarnate prints BOTH
  // lines on one card ("you have to have both of them in your attack") -
  // requiresAttackType: 'both' applies each condition to the other,
  // netting out to "either both subtotals count in full, or neither does."
  const requiresAttackType = MONSTER_IMMUNITIES[monsterId]?.requiresAttackType;
  if ((requiresAttackType === 'magic' || requiresAttackType === 'both') && magic === 0) physical = 0;
  if ((requiresAttackType === 'physical' || requiresAttackType === 'both') && physical === 0) magic = 0;
  return { physical: Math.max(0, physical), magic: Math.max(0, magic) };
}
